/**
 * 📡 SOURCE DATA CAPABILITIES
 *
 * Maps data sources (Garmin, Strava, Cedar Timer, Apple Health, Health Connect)
 * and third-party recording apps (Peloton, Runna, Nike Run Club, etc.) to the
 * specific data types they actually WRITE to each platform.
 *
 * Used by the frontend to:
 *  1. Hide expandable sections when source fundamentally can't provide that data
 *  2. Show "not available from this source" vs "data loading" vs actual data
 *  3. Adapt the metrics grid to source richness (P3-18)
 *
 * Research basis: 32 coaching apps analyzed (March 2026).
 * Key insight: the bottleneck is what apps WRITE, not what we can READ.
 *
 * Created: Sprint M35, March 2026
 */

// ── Data capability flags ──────────────────────────────────────

export interface SourceCapabilities {
  /** Per-second or per-point HR time-series (for Activity Timeline) */
  hrTimeSeries: boolean;
  /** GPS route with lat/lng trackpoints (for GPS Route Map) */
  gpsRoute: boolean;
  /** Per-km or per-lap split data */
  splits: boolean;
  /** Cadence data (running or cycling) */
  cadence: boolean;
  /** Power data (cycling, rowing) */
  power: boolean;
  /** Elevation data (gain/loss/profile) */
  elevation: boolean;
  /** HR zone distribution */
  hrZones: boolean;
  /** Running dynamics (GCT, vertical oscillation, stride length) */
  runningDynamics: boolean;
  /** Training effect / VO2 max estimates */
  trainingMetrics: boolean;
  /** Strava segments */
  segments: boolean;
  /** Power zones */
  powerZones: boolean;
}

/** Full capability — all flags true. Used as fallback for unknown sources so nothing is hidden. */
const FULL: SourceCapabilities = {
  hrTimeSeries: true,
  gpsRoute: true,
  splits: true,
  cadence: true,
  power: true,
  elevation: true,
  hrZones: true,
  runningDynamics: true,
  trainingMetrics: true,
  segments: true,
  powerZones: true,
};

/** No data — unknown source fallback */
const NONE: SourceCapabilities = {
  hrTimeSeries: false,
  gpsRoute: false,
  splits: false,
  cadence: false,
  power: false,
  elevation: false,
  hrZones: false,
  runningDynamics: false,
  trainingMetrics: false,
  segments: false,
  powerZones: false,
};

// ── Direct source capabilities ─────────────────────────────────
// These are for activities synced directly from the platform (not via HealthKit/HC)

const DIRECT_SOURCE_CAPS: Record<string, SourceCapabilities> = {
  /**
   * Garmin Direct Sync (via Coach Cedar Garmin integration)
   * The gold standard — per-second everything from the watch.
   */
  garmin: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: true,
    power: true,
    elevation: true,
    hrZones: true,
    runningDynamics: true,   // GCT, vert osc, stride length (running only)
    trainingMetrics: true,   // Training Effect, VO2 Max
    segments: false,
    powerZones: false,
  },

  /**
   * Strava Direct Sync (via Strava API)
   * Rich data: HR stream, GPS, splits, segments, suffer score.
   * Cadence/power available if recording device captured them.
   */
  strava: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: true,    // Available if device recorded it
    power: true,      // Available if power meter present
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: true,   // Strava-specific
    powerZones: false,
  },

  /**
   * Google Health Direct Sync (Pixel Watch + Fitbit, via the Google Health API).
   * v1 is INBOUND-ONLY and coarser than Garmin — a workout summary plus separate
   * heart-rate dataPoints. CONSERVATIVE caps so the UI doesn't over-promise
   * streams the v1 API may not deliver; the richer fields (route/splits/cadence/
   * elevation) are a Phase-0 live-API confirm and can be promoted then.
   */
  google_health: {
    hrTimeSeries: true,      // separate heart-rate dataPoints, correlated to the activity window
    gpsRoute: true,          // full-surface: exportExerciseTcx route (FF_GH_LOCATION_GPS; NULL-gated when absent)
    splits: true,            // full-surface: exercise.splitSummaries[] → lap_data (NULL-gated when absent)
    cadence: true,           // #37: metricsSummary.mobilityMetrics.avgCadenceStepsPerMinute (NULL-gated when absent)
    power: false,
    elevation: true,         // #37: metricsSummary.elevationGainMillimeters (NULL-gated when absent)
    hrZones: true,           // computed from HR samples (+ metricsSummary.heartRateZoneDurations)
    runningDynamics: true,   // full-surface: metricsSummary.mobilityMetrics (advanced running; NULL-gated when absent)
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Cedar In-App Timer
   * HR from Apple Watch/wearable via HealthKit bridge, GPS from phone.
   * HR samples merged into trackpoints by ingester.
   */
  cedar: {
    hrTimeSeries: true,   // Merged from raw_data HR samples
    gpsRoute: true,       // Phone GPS if location enabled
    splits: true,         // Computed from GPS trackpoints
    cadence: false,
    power: false,
    elevation: true,      // From GPS altitude
    hrZones: true,        // Computed from HR samples
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  coach_cedar: { // alias
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  in_app: { // alias
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
};

// ── Third-party app capabilities via Apple Health (HealthKit) ──
// What each app actually WRITES to HealthKit when recording a workout.
// Research: March 2026, verified against HealthKit documentation + app testing.

const HEALTHKIT_APP_CAPS: Record<string, SourceCapabilities> = {
  /**
   * Peloton (iOS) — Best-in-class HealthKit writer since Feb 2025 update.
   * Writes: HR samples, cadence (cycling), power (cycling), calories, distance.
   * Does NOT write: GPS route (indoor only), running dynamics.
   */
  peloton: {
    hrTimeSeries: true,
    gpsRoute: false,        // Indoor workouts only
    splits: false,
    cadence: true,          // Cycling cadence (RPM)
    power: true,            // Cycling power (watts)
    elevation: false,
    hrZones: true,          // Computed from HR samples
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Runna — Running app with structured plans.
   * Writes: HR samples, distance, calories.
   * GPS route writing varies by version — recent versions write HKWorkoutRoute.
   */
  runna: {
    hrTimeSeries: true,
    gpsRoute: true,         // Recent versions write HKWorkoutRoute
    splits: true,           // Computed from GPS if route available
    cadence: false,
    power: false,
    elevation: true,        // From GPS altitude in route
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Nike Run Club — Writes HR, GPS route, distance.
   * One of the better HealthKit writers for running.
   */
  'nike run club': {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  'nike+ run club': { // alias
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Zwift — Indoor cycling/running with power.
   * Writes: HR, power, cadence, calories, distance.
   * Does NOT write GPS (virtual world coordinates ≠ real GPS).
   */
  zwift: {
    hrTimeSeries: true,
    gpsRoute: false,
    splits: false,
    cadence: true,
    power: true,
    elevation: false,       // Virtual elevation, not real-world
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Strava (as HealthKit source) — Surprisingly limited.
   * Strava does NOT write HR samples to HealthKit. Only summary metrics.
   */
  strava: {
    hrTimeSeries: false,    // Strava does NOT write HR to HealthKit
    gpsRoute: true,         // Writes HKWorkoutRoute
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: false,         // No HR samples = no zones
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Garmin Connect (as HealthKit source) — Very limited.
   * Only writes summary HR (avg/max), distance, calories. NO per-second data.
   * Users with Garmin watches should use direct Garmin sync instead.
   */
  'garmin connect': {
    hrTimeSeries: false,    // Only summary HR written to HealthKit
    gpsRoute: false,        // Garmin does NOT write HKWorkoutRoute
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  garmin: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Apple Watch native Workout app — Good HealthKit writer.
   * Writes: HR samples, GPS route, distance, calories, elevation.
   */
  workout: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,         // Apple Watch doesn't expose cadence via HealthKit
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,  // VO2 written separately, not per-workout
    segments: false,
    powerZones: false,
  },
  'apple watch': {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Wahoo SYSTM / Wahoo Fitness — Cycling-focused.
   * Writes: HR, power, cadence. GPS for outdoor rides.
   */
  wahoo: {
    hrTimeSeries: true,
    gpsRoute: true,         // Outdoor only
    splits: false,
    cadence: true,
    power: true,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * TrainerRoad — Structured cycling training.
   * Writes: HR, power, cadence. No GPS (indoor trainer).
   */
  trainerroad: {
    hrTimeSeries: true,
    gpsRoute: false,
    splits: false,
    cadence: true,
    power: true,
    elevation: false,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * MapMyRun / MapMyFitness (Under Armour) — Running GPS tracker.
   * Writes: HR samples (if watch connected), GPS route, distance.
   */
  mapmyrun: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  mapmyfitness: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Adidas Running (formerly Runtastic) — Basic GPS running tracker.
   * Writes: HR (if watch), GPS, distance. Minimal advanced metrics.
   */
  'adidas running': {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  runtastic: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: true,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * WHOOP — HR-focused recovery/strain tracker.
   * Writes: HR samples, strain score. NO GPS, NO workout-type metrics.
   */
  whoop: {
    hrTimeSeries: true,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Oura — Sleep & readiness focused. Minimal workout writing.
   * Writes: HR samples during tagged activities. Very basic.
   */
  oura: {
    hrTimeSeries: true,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Fitbod — Strength training focused.
   * Writes: Duration, calories, exercise metadata. NO HR samples.
   */
  fitbod: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Strong — Strength training logger.
   * Writes: Duration, calories. NO HR samples.
   */
  strong: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * AllTrails — Hiking/trail app.
   * Writes: GPS route, elevation, distance. HR if watch connected.
   */
  alltrails: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: false,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Coros — Watch companion app. Similar to Garmin via HealthKit — limited.
   * Writes: Summary HR, distance, calories. Per-second data stays in Coros app.
   */
  coros: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Polar Flow — Watch companion app.
   * Writes: Summary HR, distance, calories. Detailed data stays in Polar app.
   */
  'polar flow': {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  polar: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Suunto — Watch companion app.
   * Similar to Garmin/Coros — summary only via HealthKit.
   */
  suunto: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Apple Fitness+ — Uses Apple Watch Workout app under the hood.
   * Same capabilities as native Apple Watch workout.
   */
  'apple fitness': {
    hrTimeSeries: true,
    gpsRoute: false,        // Fitness+ is typically indoor
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  'fitness+': {
    hrTimeSeries: true,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
};

// ── Health Connect (Android) app capabilities ──────────────────
// Android apps via Health Connect. Generally more limited than HealthKit.

const HEALTH_CONNECT_APP_CAPS: Record<string, SourceCapabilities> = {
  /**
   * Peloton (Android) — Added Health Connect support Feb 2025.
   * Writes: HR samples, cadence, power, calories.
   */
  peloton: {
    hrTimeSeries: true,
    gpsRoute: false,
    splits: false,
    cadence: true,
    power: true,
    elevation: false,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Samsung Health — Writes basic HR + GPS for outdoor activities.
   */
  'samsung health': {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: false,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Google Fit — Basic workout data. HR if watch connected.
   */
  'google fit': {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: false,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /**
   * Fitbit — Writes to Health Connect since Pixel Watch integration.
   * HR samples, GPS for outdoor, basic metrics.
   */
  fitbit: {
    hrTimeSeries: true,
    gpsRoute: true,
    splits: false,
    cadence: false,
    power: false,
    elevation: true,
    hrZones: true,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
};

// ── Generic platform-level capabilities ────────────────────────
// When we know the platform but not the specific app.

const PLATFORM_CAPS: Record<string, SourceCapabilities> = {
  /** Apple Health with unknown source app — assume minimal */
  apple_health: {
    hrTimeSeries: false,     // Can't assume — varies wildly by source app
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  healthkit: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  ios_healthkit: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /** Health Connect with unknown source app — assume minimal */
  android_health: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  health_connect: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },
  android_health_connect: {
    hrTimeSeries: false,
    gpsRoute: false,
    splits: false,
    cadence: false,
    power: false,
    elevation: false,
    hrZones: false,
    runningDynamics: false,
    trainingMetrics: false,
    segments: false,
    powerZones: false,
  },

  /** Manual completion — no device data */
  manual: { ...NONE },
};


// ── Public API ─────────────────────────────────────────────────

/**
 * Resolve the data capabilities for a given workout based on its source and
 * optional sourceAppName (the actual recording app from HealthKit/Health Connect).
 *
 * Priority:
 *  1. If source is a direct integration (garmin, strava, cedar) → use DIRECT_SOURCE_CAPS
 *  2. If sourceAppName is known → look up in platform-specific app caps
 *  3. Fall back to generic platform caps (conservative — assumes minimal data)
 *  4. Unknown → assume full capability (don't hide anything, let data checks decide)
 */
export function getSourceCapabilities(
  source: string | undefined,
  sourceAppName?: string | undefined,
): SourceCapabilities {
  const normalizedSource = (source || '').toLowerCase().trim();
  const normalizedApp = (sourceAppName || '').toLowerCase().trim();

  // 1. Direct integrations — we control the data pipeline, known capabilities
  if (DIRECT_SOURCE_CAPS[normalizedSource] && !isPassthroughSource(normalizedSource)) {
    return DIRECT_SOURCE_CAPS[normalizedSource];
  }

  // 2. Source is HealthKit/Health Connect — check for specific app
  if (normalizedApp) {
    const isAndroid = normalizedSource.includes('android') || normalizedSource.includes('health_connect');
    const appCaps = isAndroid
      ? matchAppCaps(normalizedApp, HEALTH_CONNECT_APP_CAPS)
      : matchAppCaps(normalizedApp, HEALTHKIT_APP_CAPS);
    if (appCaps) return appCaps;
  }

  // 3. Known platform, unknown app → conservative defaults
  if (PLATFORM_CAPS[normalizedSource]) {
    return PLATFORM_CAPS[normalizedSource];
  }

  // 4. Unknown source → don't restrict anything, let data presence checks decide
  return { ...FULL };
}

/**
 * Check if a source is a "passthrough" platform (HealthKit / Health Connect)
 * rather than a direct integration. These need sourceAppName to determine capabilities.
 */
function isPassthroughSource(source: string): boolean {
  return (
    source.includes('apple_health') ||
    source.includes('healthkit') ||
    source.includes('ios_healthkit') ||
    source.includes('android_health') ||
    source.includes('health_connect')
  );
}

/**
 * Fuzzy-match a sourceAppName against an app capabilities map.
 * Handles partial matches (e.g., "com.onepeloton.callisto" matches "peloton").
 * Uses longest-match-wins to avoid false positives with short keys.
 */
function matchAppCaps(
  appName: string,
  capsMap: Record<string, SourceCapabilities>,
): SourceCapabilities | null {
  // Exact match first
  if (capsMap[appName]) return capsMap[appName];

  // Partial match — longest key match wins to avoid false positives
  // (e.g., "strong" shouldn't match "armstrong fitness")
  let bestMatch: { key: string; caps: SourceCapabilities } | null = null;
  for (const [key, caps] of Object.entries(capsMap)) {
    if (appName.includes(key)) {
      if (!bestMatch || key.length > bestMatch.key.length) {
        bestMatch = { key, caps };
      }
    }
  }

  return bestMatch?.caps ?? null;
}

/**
 * Check if a specific expandable section should be visible for this source.
 * Returns true if the source CAN provide this data type (even if data isn't present yet).
 * Returns false if the source fundamentally cannot provide it.
 *
 * This is used ALONGSIDE data-presence checks: a section is shown only when
 * BOTH the source can provide it AND the data actually exists.
 */
export function canSourceProvide(
  section: string,
  capabilities: SourceCapabilities,
): boolean {
  switch (section) {
    case 'activity-timeline':
      return capabilities.hrTimeSeries;
    case 'gps-route':
      return capabilities.gpsRoute;
    case 'splits':
      return capabilities.gpsRoute || capabilities.splits;
    case 'elevation':
      return capabilities.gpsRoute || capabilities.elevation;
    case 'hr-zones':
      return capabilities.hrTimeSeries || capabilities.hrZones;
    case 'power-cadence':
      return capabilities.power || capabilities.cadence;
    case 'segments':
      return capabilities.segments;
    case 'running-dynamics':
      return capabilities.runningDynamics;
    case 'training-metrics':
      return capabilities.trainingMetrics;
    // These sections don't depend on source capabilities
    case 'how-it-went':
    case 'phases':
    case 'exercises':
    case 'hiit':
      return true;
    default:
      return true; // Unknown section → don't hide
  }
}

/**
 * Get a human-readable data quality summary for a source.
 * Useful for showing users what to expect from their connected devices.
 */
export function getSourceDataQuality(
  source: string | undefined,
  sourceAppName?: string | undefined,
): 'full' | 'rich' | 'moderate' | 'basic' | 'minimal' {
  const caps = getSourceCapabilities(source, sourceAppName);
  const trueCount = Object.values(caps).filter(Boolean).length;

  if (trueCount >= 9) return 'full';
  if (trueCount >= 6) return 'rich';
  if (trueCount >= 3) return 'moderate';
  if (trueCount >= 1) return 'basic';
  return 'minimal';
}

/**
 * Get metrics that should be HIDDEN in PostCompletionMetricsGrid because
 * the source can't provide them. Prevents showing empty/zero cells.
 *
 * Returns a Set of metric labels that should be excluded.
 */
export function getHiddenMetrics(
  source: string | undefined,
  sourceAppName?: string | undefined,
): Set<string> {
  const caps = getSourceCapabilities(source, sourceAppName);
  const hidden = new Set<string>();

  if (!caps.cadence) {
    hidden.add('Avg Cadence');
    hidden.add('Max Cadence');
  }
  if (!caps.power) {
    hidden.add('Avg Power');
    hidden.add('Max Power');
    hidden.add('Normalized Power');
  }
  if (!caps.trainingMetrics) {
    // 2026-04-28: 'VO2 Max' removed from this filter — the tile now
    // renders session.currentVo2Max, which is a rolling cross-source
    // resolved value (Garmin > Apple > HC > Fitbit > 30d EWMA), not
    // a per-workout estimate. The per-workout vo2_max_estimate it
    // replaced was retired in the April 2026 device-import parity
    // mission. Training Effect stays — that's still per-workout.
    hidden.add('Training Effect');
  }
  if (!caps.runningDynamics) {
    hidden.add('GCT');
    hidden.add('Vert. Osc.');
    hidden.add('Stride');
  }
  if (!caps.segments) {
    hidden.add('Suffer Score');
  }

  return hidden;
}
